import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  Tabs,
  Tab,
  Pagination,
} from '@mui/material';
import {
  Send as SendIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Notifications as NotificationIcon,
  TrendingUp as StatsIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { pushNotificationAPI } from '../../../services/api';
import { toast } from 'react-toastify';

const PushNotificationManagement = () => {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'general',
    target_audience: 'all',
    target_user_id: '',
    priority: 2,
  });

  const notificationTypes = [
    { value: 'general', label: 'General', color: 'primary' },
    { value: 'result', label: 'Result', color: 'success' },
    { value: 'bet_update', label: 'Bet Update', color: 'info' },
    { value: 'wallet', label: 'Wallet', color: 'warning' },
    { value: 'admin', label: 'Admin', color: 'error' },
  ];

  const targetAudiences = [
    { value: 'all', label: 'All Users', icon: <PeopleIcon />, description: 'Sent via FCM topic "all_users"' },
    { value: 'specific_user', label: 'Specific User', icon: <PersonIcon />, description: 'Sent to user\'s registered devices' },
    { value: 'admin_only', label: 'Admin Only', icon: <NotificationIcon />, description: 'Sent via FCM topic "admin_users"' },
  ];

  useEffect(() => {
    if (selectedTab === 0) {
      fetchNotifications();
    } else {
      fetchStats();
    }
  }, [selectedTab, currentPage]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await pushNotificationAPI.getAllNotifications({ 
        page: currentPage, 
        limit: 20 
      });
      if (response.data.success) {
        setNotifications(response.data.data.notifications);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (error) {
      toast.error('Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await pushNotificationAPI.getNotificationStats();
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = () => {
    setFormData({
      title: '',
      message: '',
      type: 'general',
      target_audience: 'all',
      target_user_id: '',
      priority: 2,
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const submitData = {
        ...formData,
        target_user_id: formData.target_audience === 'specific_user' ? parseInt(formData.target_user_id) : undefined,
      };

      const response = await pushNotificationAPI.sendPushNotification(submitData);
      if (response.data.success) {
        const fcmInfo = response.data.fcm;

        // Show detailed success message with FCM info
        if (fcmInfo && fcmInfo.sent) {
          toast.success(
            `Notification sent successfully! Delivered to ${fcmInfo.successCount || 0} device(s).${fcmInfo.failureCount > 0 ? ` Failed: ${fcmInfo.failureCount}` : ''
            }`
          );
        } else if (response.data.warning) {
          toast.warning(`Notification created but: ${response.data.warning}`);
        } else {
          toast.success('Notification sent successfully');
        }

        setOpenDialog(false);
        if (selectedTab === 0) {
          fetchNotifications();
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        setLoading(true);
        const response = await pushNotificationAPI.deleteNotification(id);
        if (response.data.success) {
          toast.success('Notification deleted successfully');
          fetchNotifications();
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Delete failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const getTypeChip = (type) => {
    const typeConfig = notificationTypes.find(t => t.value === type) || notificationTypes[0];
    return <Chip label={typeConfig.label} color={typeConfig.color} size="small" />;
  };

  const getAudienceChip = (audience) => {
    const audienceConfig = targetAudiences.find(a => a.value === audience) || targetAudiences[0];
    return <Chip label={audienceConfig.label} size="small" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const StatsCards = () => (
    <Grid container spacing={3} mb={3}>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Total Notifications
            </Typography>
            <Typography variant="h4">
              {stats.total || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Sent
            </Typography>
            <Typography variant="h4" color="success.main">
              {stats.sent || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Pending
            </Typography>
            <Typography variant="h4" color="warning.main">
              {stats.pending || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography color="textSecondary" gutterBottom>
              Success Rate
            </Typography>
            <Typography variant="h4" color="primary.main">
              {stats.total ? Math.round((stats.sent / stats.total) * 100) : 0}%
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Push Notification Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Send real-time push notifications via Firebase Cloud Messaging (FCM)
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={selectedTab === 0 ? fetchNotifications : fetchStats}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={handleSendNotification}
            disabled={loading}
          >
            Send Notification
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Tabs 
            value={selectedTab} 
            onChange={(e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mb: 3 }}
          >
            <Tab label="All Notifications" />
            <Tab label="Statistics" />
          </Tabs>

          {selectedTab === 1 && <StatsCards />}

          {selectedTab === 0 && (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Audience</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Sent At</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifications.map((notification) => (
                      <TableRow key={notification.id}>
                        <TableCell>
                          <Typography variant="subtitle2">{notification.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {notification.message.length > 50 
                              ? `${notification.message.substring(0, 50)}...` 
                              : notification.message}
                          </Typography>
                        </TableCell>
                        <TableCell>{getTypeChip(notification.type)}</TableCell>
                        <TableCell>{getAudienceChip(notification.target_audience)}</TableCell>
                        <TableCell>
                          <Chip 
                            label={notification.priority === 3 ? 'High' : notification.priority === 2 ? 'Medium' : 'Low'} 
                            color={notification.priority >= 3 ? 'error' : notification.priority >= 2 ? 'warning' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={notification.is_sent ? 'Sent' : 'Pending'} 
                            color={notification.is_sent ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {notification.sent_at ? formatDate(notification.sent_at) : 'Not sent'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Delete">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteNotification(notification.id)}
                              disabled={loading}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {notifications.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary">No notifications found</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(e, page) => setCurrentPage(page)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Send Notification Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Send Push Notification
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Title"
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="e.g., 🎯 New Result Available!"
              helperText="Add emojis to make it more engaging"
            />
            
            <TextField
              label="Message"
              fullWidth
              multiline
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              placeholder="Enter your notification message here..."
              helperText={`${formData.message.length} characters`}
            />
            
            <Box display="flex" gap={2}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  label="Type"
                >
                  {notificationTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={formData.target_audience}
                  onChange={(e) => setFormData({ ...formData, target_audience: e.target.value })}
                  label="Target Audience"
                >
                  {targetAudiences.map((audience) => (
                    <MenuItem key={audience.value} value={audience.value}>
                      <Box>
                        <Typography>{audience.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {audience.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box display="flex" gap={2}>
              {formData.target_audience === 'specific_user' && (
                <TextField
                  label="Target User ID"
                  type="number"
                  fullWidth
                  value={formData.target_user_id}
                  onChange={(e) => setFormData({ ...formData, target_user_id: e.target.value })}
                  required
                  helperText="Enter the numeric user ID"
                />
              )}
              
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value={1}>Low (1)</MenuItem>
                  <MenuItem value={2}>Medium (2)</MenuItem>
                  <MenuItem value={3}>High (3)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleSubmit}
            disabled={loading || !formData.title || !formData.message || 
              (formData.target_audience === 'specific_user' && !formData.target_user_id)}
          >
            Send Notification
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PushNotificationManagement;